import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Inbox, Loader2, CheckCircle2, X, RotateCcw, AlertTriangle, MapPin, DollarSign } from 'lucide-react';

const API = '/api';

/**
 * Fitters asking to be promoted, from the website (backend: promote_requests.py).
 *
 * Randall, 21 Oct 2026: "a way that a fitter can ask to be promoted and also contribute extra dollars if they want
 * to". The fitter pays by card on the website when they ask (Stax in US dollars, Stripe in Australian dollars); the
 * Hub never moves money. Randall answers each request: Approve with H-G's share ("You decide per request"), or
 * Decline. Money paid on a request that is declined or does not run is owed back: refund it in Stax or Stripe, then
 * mark it refunded here. The fitter's page shows where their request stands, never the reasons written here.
 */

const STATUS = {
  new:      { label: 'Waiting on you', cls: 'text-amber-300 border-amber-400/40 bg-amber-400/10' },
  approved: { label: 'Approved',       cls: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  running:  { label: 'Running',        cls: 'text-blue-300 border-blue-400/40 bg-blue-400/10' },
  declined: { label: 'Declined',       cls: 'text-hg-text-dim border-hg-border bg-hg-surface/40' },
  not_run:  { label: 'Did not run',    cls: 'text-hg-text-dim border-hg-border bg-hg-surface/40' },
  refunded: { label: 'Refunded',       cls: 'text-hg-text-dim border-hg-border bg-hg-surface/40' },
};

function money(amount, currency) {
  const n = Number(amount || 0);
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || 'USD'}`;
}

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function errorText(e) {
  return e?.response?.data?.detail || e?.message || 'Something went wrong.';
}

export default function PromoteRequests({ onMakeAd } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/promote-requests`);
      setData(r.data);
      setError('');
    } catch (e) {
      // A failed read is "could not read", never "no requests" (CLAUDE.md §11).
      setData(null);
      setError(`Could not read the requests: ${errorText(e)}`);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return <div data-testid="promote-requests-error" className="text-sm font-sans text-red-400 border border-red-500/40 bg-red-500/10 rounded-sm p-3">{error}</div>;
  }
  if (!data) {
    return <div className="flex items-center gap-2 text-sm font-sans text-hg-text-dim"><Loader2 size={14} className="animate-spin" /> Loading requests…</div>;
  }
  const rows = data.requests || [];
  return (
    <div className="space-y-4" data-testid="promote-requests">
      <p className="text-sm font-sans text-hg-text-dim max-w-3xl">
        Fitters ask from the website's Get promoted page, and may put in their own money, paid by card when they ask
        (Stax in US dollars, Stripe in Australian dollars). Approve with H-G's share, or decline. Money paid on a request
        you decline, or that does not run, is owed back: refund it in Stax or Stripe, then mark it refunded here. The
        fitter sees where their request stands, never what you write here.
      </p>
      {rows.length === 0 && (
        <div className="flex items-center gap-2 text-sm font-sans text-hg-text-dim border border-hg-border rounded-sm p-4">
          <Inbox size={14} /> No fitter has asked yet.
        </div>
      )}
      {rows.map((r) => <RequestCard key={r.id} r={r} onChanged={load} onMakeAd={onMakeAd} />)}
    </div>
  );
}

function RequestCard({ r, onChanged, onMakeAd }) {
  const [mode, setMode] = useState(null); // 'approve' | 'decline' | 'not-run' | 'refunded'
  const [share, setShare] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const f = r.fitter || {};
  const c = r.contribution || {};
  const st = STATUS[r.shown_status] || STATUS[r.status] || STATUS.new;
  const where = c.gateway === 'stripe' ? 'Stripe' : 'Stax';

  const act = async (path, body) => {
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API}/promote-requests/${encodeURIComponent(r.id)}/${path}`, body);
      setMode(null);
      setText('');
      setShare('');
      await onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const shareOk = share.trim() !== '' && Number.isFinite(Number(share)) && Number(share) >= 0;

  return (
    <div data-testid={`promote-request-${r.id}`} className="border border-hg-border rounded-sm bg-hg-surface/30 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-sm font-sans text-hg-text font-medium">{f.name || f.email}</h3>
          <p className="text-xs font-sans text-hg-text-dim">
            {[f.city, f.state, f.market === 'au' ? 'Australia' : 'USA'].filter(Boolean).join(', ')} · {f.email}{f.phone ? ` · ${f.phone}` : ''}
          </p>
          <p className="text-[11px] font-sans text-hg-text-dim">Asked {when(r.requested_at || r.received_at)}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10px] font-sans uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
          {r.refund_owed && (
            <span data-testid="refund-owed" className="text-[10px] font-sans uppercase tracking-wider border rounded-sm px-1.5 py-0.5 text-red-300 border-red-400/40 bg-red-400/10">Refund owed</span>
          )}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 text-xs font-sans text-hg-text">
        <p className="flex items-start gap-1.5"><MapPin size={12} className="mt-0.5 shrink-0" />
          <span>{r.area || '—'} · within {r.radius?.value} {r.radius?.unit === 'km' ? 'km' : 'miles'}</span></p>
        <p className="flex items-start gap-1.5"><DollarSign size={12} className="mt-0.5 shrink-0" />
          <span data-testid="contribution">
            {c.amount > 0
              ? <>Put in {money(c.amount, c.currency)}, paid by card through {where}<br />
                  <span className="text-hg-text-dim">Payment {c.payment_id} · {when(c.paid_at)}</span></>
              : 'No money put in'}
          </span></p>
        <p>May use their name: <strong>{r.consent?.name ? 'yes' : 'no'}</strong> · their photo: <strong>{r.consent?.photo ? 'yes' : 'no'}</strong></p>
        {r.hg_share && <p>H-G's share: <strong>{money(r.hg_share.amount, r.hg_share.currency)}</strong></p>}
      </div>
      {r.notes && <p className="text-xs font-sans text-hg-text whitespace-pre-wrap border-l-2 border-hg-border pl-2">{r.notes}</p>}
      {f.page_url && <a className="text-xs font-sans text-hg-green" href={f.page_url} target="_blank" rel="noreferrer">Their Find a Fitter page</a>}

      {r.refund_owed && (
        <p data-testid="refund-help" className="text-xs font-sans text-red-300 flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {money(c.amount, c.currency)} is owed back. Refund it in {where} (payment {c.payment_id}), then mark it refunded.
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {r.status === 'new' && mode === null && (
          <>
            <button type="button" disabled={busy} onClick={() => setMode('approve')} className="text-xs font-sans px-3 py-1.5 rounded-sm bg-hg-green text-white inline-flex items-center gap-1"><CheckCircle2 size={12} /> Approve</button>
            <button type="button" disabled={busy} onClick={() => setMode('decline')} className="text-xs font-sans px-3 py-1.5 rounded-sm border border-hg-border text-hg-text inline-flex items-center gap-1"><X size={12} /> Decline</button>
          </>
        )}
        {r.status === 'approved' && mode === null && (
          <>
            {onMakeAd && <button type="button" onClick={() => onMakeAd(r.promoted_fitter_id)} className="text-xs font-sans px-3 py-1.5 rounded-sm bg-hg-green text-white">Make the ad</button>}
            <button type="button" disabled={busy} onClick={() => setMode('not-run')} className="text-xs font-sans px-3 py-1.5 rounded-sm border border-hg-border text-hg-text">It did not run</button>
          </>
        )}
        {r.refund_owed && mode === null && (
          <button type="button" disabled={busy} onClick={() => setMode('refunded')} className="text-xs font-sans px-3 py-1.5 rounded-sm border border-red-400/40 text-red-300 inline-flex items-center gap-1"><RotateCcw size={12} /> Mark refunded</button>
        )}
      </div>

      {mode === 'approve' && (
        <div className="space-y-2 border-t border-hg-border pt-3" data-testid="approve-form">
          <label className="block text-xs font-sans text-hg-text">
            H-G's share ({c.currency || 'USD'})
            <input type="number" min="0" step="1" value={share} onChange={(e) => setShare(e.target.value)}
                   className="block mt-1 w-40 bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-sm" />
          </label>
          <p className="text-[11px] font-sans text-hg-text-dim">
            {c.amount > 0 ? `The fitter put in ${money(c.amount, c.currency)}. ` : 'The fitter put in no money. '}
            Approving adds them to the Fitter Directory if they are not there; the ad is made under Ad Drafts.
          </p>
          <div className="flex gap-2">
            <button type="button" disabled={busy || !shareOk} onClick={() => act('approve', { hg_share: Number(share) })} className="text-xs font-sans px-3 py-1.5 rounded-sm bg-hg-green text-white disabled:opacity-40">Approve</button>
            <button type="button" onClick={() => setMode(null)} className="text-xs font-sans px-3 py-1.5 rounded-sm border border-hg-border text-hg-text">Cancel</button>
          </div>
        </div>
      )}
      {(mode === 'decline' || mode === 'not-run' || mode === 'refunded') && (
        <div className="space-y-2 border-t border-hg-border pt-3" data-testid={`${mode}-form`}>
          <label className="block text-xs font-sans text-hg-text">
            {mode === 'refunded' ? `Note (optional): the ${where} refund` : 'Why (for the record; the fitter is not shown it)'}
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                      className="block mt-1 w-full bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-sm" />
          </label>
          {mode !== 'refunded' && c.amount > 0 && (
            <p className="text-[11px] font-sans text-red-300">The {money(c.amount, c.currency)} they paid will be owed back.</p>
          )}
          <div className="flex gap-2">
            <button type="button" disabled={busy || (mode !== 'refunded' && !text.trim())}
                    onClick={() => act(mode, mode === 'refunded' ? { note: text } : { reason: text })}
                    className="text-xs font-sans px-3 py-1.5 rounded-sm bg-hg-green text-white disabled:opacity-40">
              {mode === 'decline' ? 'Decline' : mode === 'not-run' ? 'Mark as not run' : 'Mark refunded'}
            </button>
            <button type="button" onClick={() => setMode(null)} className="text-xs font-sans px-3 py-1.5 rounded-sm border border-hg-border text-hg-text">Cancel</button>
          </div>
        </div>
      )}
      {error && <p className="text-xs font-sans text-red-400">{error}</p>}

      {(r.history || []).length > 0 && (
        <details className="text-[11px] font-sans text-hg-text-dim">
          <summary className="cursor-pointer">History</summary>
          <ul className="mt-1 space-y-0.5">
            {r.history.map((h, i) => <li key={i}>{when(h.at)} · {h.by}: {h.what}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}
